// Analytics Service - Tracks user behavior and interactions
class AnalyticsService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.interactions = [];
    this.pageViews = [];
    this.scrollDepth = 0;
    this.maxScrollDepth = 0;
    this.isActive = true;
    this.lastActivity = Date.now();
    this.trackedSections = new Set(); // Track which sections have been viewed
    this.apiEndpoint = 'https://rar.sillydev.lol/api/analytics'; // Backend endpoint for analytics data
    
    this.init();
  }

  generateSessionId() {
    // Try to get existing session ID from sessionStorage
    const existingSessionId = sessionStorage.getItem('analytics_session_id');
    
    if (existingSessionId) {
      return existingSessionId;
    }
    
    // Create new session ID
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', newSessionId);
    
    return newSessionId;
  }

  init() {
    // Track page visibility
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.sendEvent('page_hidden');
      } else {
        this.sendEvent('page_visible');
      }
    });

    // Track scroll depth
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.trackScrollDepth();
      }, 100);
    });

    // Track clicks
    document.addEventListener('click', (e) => {
      this.trackClick(e);
    });

    // Track time on page (send every 10 seconds)
    setInterval(() => {
      this.sendTimeUpdate();
    }, 10000);

    // Track mouse movement (for activity detection)
    let mouseTimeout;
    document.addEventListener('mousemove', () => {
      this.lastActivity = Date.now();
      this.isActive = true;
      clearTimeout(mouseTimeout);
      mouseTimeout = setTimeout(() => {
        this.isActive = false;
      }, 30000); // 30 seconds of inactivity
    });

    // Track keyboard activity
    document.addEventListener('keydown', () => {
      this.lastActivity = Date.now();
      this.isActive = true;
    });

    // Send session start
    this.sendEvent('session_start', {
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      referrer: document.referrer || 'direct',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    // Send session end on page unload
    window.addEventListener('beforeunload', () => {
      this.sendEvent('session_end', {
        duration: Date.now() - this.startTime,
        interactions: this.interactions.length,
        maxScrollDepth: this.maxScrollDepth
      });
    });
  }

  trackScrollDepth() {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;
    const scrollPercent = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
    
    this.scrollDepth = scrollPercent;
    if (scrollPercent > this.maxScrollDepth) {
      this.maxScrollDepth = scrollPercent;
      
      // Track milestone scrolls
      if (scrollPercent >= 25 && !this.scrollMilestones?.includes(25)) {
        this.scrollMilestones = [...(this.scrollMilestones || []), 25];
        this.sendEvent('scroll_milestone', { depth: 25 });
      }
      if (scrollPercent >= 50 && !this.scrollMilestones?.includes(50)) {
        this.scrollMilestones = [...(this.scrollMilestones || []), 50];
        this.sendEvent('scroll_milestone', { depth: 50 });
      }
      if (scrollPercent >= 75 && !this.scrollMilestones?.includes(75)) {
        this.scrollMilestones = [...(this.scrollMilestones || []), 75];
        this.sendEvent('scroll_milestone', { depth: 75 });
      }
      if (scrollPercent >= 100 && !this.scrollMilestones?.includes(100)) {
        this.scrollMilestones = [...(this.scrollMilestones || []), 100];
        this.sendEvent('scroll_milestone', { depth: 100 });
      }
    }
  }

  trackClick(e) {
    const target = e.target;
    const tagName = target.tagName.toLowerCase();
    const id = target.id || null;
    const className = target.className || null;
    const text = target.textContent?.substring(0, 50) || null;
    
    const interaction = {
      type: 'click',
      element: tagName,
      id,
      className,
      text,
      timestamp: Date.now(),
      x: e.clientX,
      y: e.clientY
    };
    
    this.interactions.push(interaction);
    
    this.sendEvent('click', interaction);
  }

  trackSectionView(sectionId) {
    // Only track each section once per session
    if (this.trackedSections.has(sectionId)) {
      return;
    }
    
    this.trackedSections.add(sectionId);
    
    const pageView = {
      section: sectionId,
      timestamp: Date.now(),
      url: window.location.href
    };
    
    this.pageViews.push(pageView);
    this.sendEvent('section_view', pageView);
  }

  sendTimeUpdate() {
    const timeOnPage = Date.now() - this.startTime;
    this.sendEvent('time_update', {
      duration: timeOnPage,
      isActive: this.isActive,
      scrollDepth: this.scrollDepth,
      interactions: this.interactions.length
    });
  }

  sendEvent(eventType, data = {}) {
    const event = {
      sessionId: this.sessionId,
      eventType,
      timestamp: Date.now(),
      url: window.location.href,
      ...data
    };

    // Send to backend
    fetch(this.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
      keepalive: true
    }).catch(err => {
      console.error('Analytics error:', err);
    });
  }

  // Custom event tracking
  trackCustomEvent(eventName, data = {}) {
    this.sendEvent('custom_event', {
      eventName,
      ...data
    });
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

export default analytics;
